import { getGameRuntimeConfig } from "../api/config";
import { clearAndroidDatabase, importIntoStorage, usesAndroidSQLite, withAndroidSQLite } from "./android-sqlite";
export { usesAndroidSQLite } from "./android-sqlite";

export interface StateStorage {
  getItem: (name: string) => string | null | Promise<string | null>;
  setItem: (name: string, value: string) => unknown | Promise<unknown>;
  removeItem: (name: string) => unknown | Promise<unknown>;
}

interface StringSaveRecord {
  key: string;
  value: string;
  packageId: number;
  updatedAt: string;
}

interface ValueSaveRecord<T> {
  key: string;
  value: T;
  packageId: number;
  updatedAt: string;
}

export interface IndexedDbStorageOptions {
  dbName?: string;
  storeName?: string;
  packageId?: number;
}

export interface GameSaveStorageOptions extends IndexedDbStorageOptions {
  saveKey: string;
  legacySources?: GameSaveLegacySource[];
}

const DEFAULT_STORE_NAME = "keyValueSaves";
const DEFAULT_SESSION_SAVE_KEY = "session-v1";

export interface GameSaveLegacySource extends IndexedDbStorageOptions {
  saveKey?: string;
}

function canUseIndexedDb() {
  return typeof window !== "undefined" && "indexedDB" in window;
}

function canUseLocalStorage() {
  return typeof window !== "undefined" && "localStorage" in window;
}

function readLocalStorage(key: string) {
  if (!canUseLocalStorage()) return null;

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string) {
  if (!canUseLocalStorage()) return;

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // localStorage is only a fallback; failed writes should not block gameplay.
  }
}

function removeLocalStorage(key: string) {
  if (!canUseLocalStorage()) return;

  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures.
  }
}

function openDatabase(dbName: string, storeName: string, usesKeyPath: boolean) {
  if (!canUseIndexedDb()) {
    return Promise.reject(new Error("IndexedDB is not available"));
  }

  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = window.indexedDB.open(dbName, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(storeName)) {
        if (usesKeyPath) {
          db.createObjectStore(storeName, { keyPath: "key" });
        } else {
          db.createObjectStore(storeName);
        }
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Failed to open IndexedDB"));
    request.onblocked = () => reject(new Error("IndexedDB upgrade is blocked"));
  });
}

async function withStore<T>(dbName: string, storeName: string, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>, usesKeyPath = true) {
  const db = await openDatabase(dbName, storeName, usesKeyPath);

  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(storeName, mode);
      const request = run(transaction.objectStore(storeName));

      // A request can succeed before the transaction aborts (e.g. disk full).
      // Saving/migration is complete only after the transaction commits.
      transaction.oncomplete = () => resolve(request.result);
      transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
      transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
      request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
    });
  } finally {
    db.close();
  }
}

function resolveOptions(options: IndexedDbStorageOptions = {}) {
  const config = options.packageId ? undefined : getGameRuntimeConfig();
  const packageId = options.packageId ?? config?.packageId ?? 0;

  return {
    dbName: options.dbName ?? `apk-games-${packageId}`,
    storeName: options.storeName ?? DEFAULT_STORE_NAME,
    packageId
  };
}

/** Inspect an old store without upgrading/creating it. Missing stores are normal
 * during package migrations; real IO/transaction errors must remain observable.
 */
function readLegacyRecord<T>(dbName: string, storeName: string, key: string): Promise<T | undefined> {
  if (!canUseIndexedDb()) return Promise.resolve(undefined);
  return new Promise((resolve, reject) => {
    let missingDatabase = false;
    const request = window.indexedDB.open(dbName);
    request.onupgradeneeded = () => {
      missingDatabase = true;
      request.transaction?.abort();
    };
    request.onerror = () => missingDatabase ? resolve(undefined) : reject(request.error ?? new Error("Legacy save read failed"));
    request.onblocked = () => reject(new Error("Legacy save database is blocked"));
    request.onsuccess = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(storeName)) { db.close(); resolve(undefined); return; }
      try {
        const transaction = db.transaction(storeName, "readonly");
        const read = transaction.objectStore(storeName).get(key);
        transaction.oncomplete = () => { db.close(); resolve(read.result as T | undefined); };
        transaction.onabort = () => { db.close(); reject(transaction.error ?? new Error("Legacy save read aborted")); };
        transaction.onerror = () => { db.close(); reject(transaction.error ?? new Error("Legacy save read failed")); };
      } catch (error) { db.close(); reject(error); }
    };
  });
}

export function createIndexedDbStateStorage(options: IndexedDbStorageOptions = {}): StateStorage {
  const resolved = resolveOptions(options);

  async function readRecord(key: string) {
    return withStore<StringSaveRecord | undefined>(resolved.dbName, resolved.storeName, "readonly", (store) => store.get(key));
  }

  async function writeRecord(record: StringSaveRecord) {
    await withStore<IDBValidKey>(resolved.dbName, resolved.storeName, "readwrite", (store) => store.put(record));
  }

  async function deleteRecord(key: string) {
    await withStore<undefined>(resolved.dbName, resolved.storeName, "readwrite", (store) => store.delete(key));
  }

  const browser: StateStorage = {
    async getItem(key) {
      try {
        const record = await readRecord(key);
        if (record?.value !== undefined) return record.value;

        const legacyValue = readLocalStorage(key);
        if (legacyValue) {
          await writeRecord({
            key,
            value: legacyValue,
            packageId: resolved.packageId,
            updatedAt: new Date().toISOString()
          });
        }

        return legacyValue;
      } catch {
        return readLocalStorage(key);
      }
    },
    async setItem(key, value) {
      try {
        await writeRecord({
          key,
          value,
          packageId: resolved.packageId,
          updatedAt: new Date().toISOString()
        });

        removeLocalStorage(key);
      } catch {
        writeLocalStorage(key, value);
      }
    },
    async removeItem(key) {
      try {
        await deleteRecord(key);
      } catch {
        // Keep remove best-effort; local fallback is still cleared below.
      }

      removeLocalStorage(key);
    }
  };

  return withAndroidSQLite(resolved, browser, async (key) => {
    // Read-only migration: never delete or rewrite the pre-upgrade copy.
    const record = await readLegacyRecord<StringSaveRecord>(resolved.dbName, resolved.storeName, key);
    return record?.value ?? readLocalStorage(key);
  });
}

export function createMigratingStateStorage(primary: StateStorage, legacyStorages: StateStorage[] = []): StateStorage {
  return {
    async getItem(key) {
      const value = await primary.getItem(key);
      if (value !== null) return value;

      for (const legacy of legacyStorages) {
        const legacyValue = await legacy.getItem(key);
        if (legacyValue === null) continue;
        return importIntoStorage(primary, key, legacyValue);
      }

      return null;
    },
    setItem(key, value) {
      return primary.setItem(key, value);
    },
    async removeItem(key) {
      await primary.removeItem(key);
      await Promise.allSettled(legacyStorages.map((legacy) => legacy.removeItem(key)));
    }
  };
}

export function createLegacyIndexedDbStateStorage(options: Required<Pick<IndexedDbStorageOptions, "dbName" | "storeName">>): StateStorage {
  async function readValue(key: string) {
    return readLegacyRecord<string>(options.dbName, options.storeName, key);
  }

  async function writeValue(key: string, value: string) {
    await withStore<IDBValidKey>(options.dbName, options.storeName, "readwrite", (store) => store.put(value, key), false);
  }

  async function deleteValue(key: string) {
    await withStore<undefined>(options.dbName, options.storeName, "readwrite", (store) => store.delete(key), false);
  }

  return {
    async getItem(key) {
      if (!canUseIndexedDb()) return readLocalStorage(key);

      try {
        return (await readValue(key)) ?? readLocalStorage(key);
      } catch {
        return readLocalStorage(key);
      }
    },
    async setItem(key, value) {
      if (!canUseIndexedDb()) {
        writeLocalStorage(key, value);
        return;
      }

      try {
        await writeValue(key, value);
        removeLocalStorage(key);
      } catch {
        writeLocalStorage(key, value);
      }
    },
    async removeItem(key) {
      removeLocalStorage(key);
      if (!canUseIndexedDb()) return;

      try {
        await deleteValue(key);
      } catch {
        // localStorage fallback was already cleared.
      }
    }
  };
}

function createBrowserGameSaveStorage<T>(options: GameSaveStorageOptions) {
  const resolved = resolveOptions(options);
  const legacySources = options.legacySources ?? [];

  function resolveLegacySource(source: GameSaveLegacySource) {
    return {
      ...resolveOptions(source),
      saveKey: source.saveKey ?? options.saveKey
    };
  }

  async function loadGameSave(): Promise<T | undefined> {
    const record = await withStore<ValueSaveRecord<T> | undefined>(resolved.dbName, resolved.storeName, "readonly", (store) => store.get(options.saveKey));
    if (record?.value !== undefined) return record.value;

    for (const source of legacySources) {
      const legacy = resolveLegacySource(source);
      try {
        const legacyRecord = await withStore<ValueSaveRecord<T> | undefined>(legacy.dbName, legacy.storeName, "readonly", (store) => store.get(legacy.saveKey));
        if (legacyRecord?.value === undefined) continue;
        await saveGameState(legacyRecord.value);
        return legacyRecord.value;
      } catch {
        // 旧库只作为迁移来源，读取失败不能影响新库加载。
      }
    }

    return undefined;
  }

  async function saveGameState(value: T) {
    const record: ValueSaveRecord<T> = {
      key: options.saveKey,
      value,
      packageId: resolved.packageId,
      updatedAt: new Date().toISOString()
    };

    await withStore<IDBValidKey>(resolved.dbName, resolved.storeName, "readwrite", (store) => store.put(record));
  }

  async function clearGameSave() {
    await withStore<undefined>(resolved.dbName, resolved.storeName, "readwrite", (store) => store.delete(options.saveKey));
    await Promise.allSettled(
      legacySources.map(async (source) => {
        const legacy = resolveLegacySource(source);
        await withStore<undefined>(legacy.dbName, legacy.storeName, "readwrite", (store) => store.delete(legacy.saveKey));
      })
    );
  }

  return { loadGameSave, saveGameState, clearGameSave };
}

export function createGameSaveStorage<T>(options: GameSaveStorageOptions) {
  const resolved = resolveOptions(options);
  const browser = createBrowserGameSaveStorage<T>(options);
  const storage = withAndroidSQLite(resolved, {
    async getItem() {
      const value = await browser.loadGameSave();
      return value === undefined ? null : JSON.stringify(value);
    },
    setItem: (_key, value) => browser.saveGameState(JSON.parse(value) as T),
    removeItem: () => browser.clearGameSave(),
  }, async () => {
    const sources = [{ ...resolved, saveKey: options.saveKey }, ...(options.legacySources ?? []).map((source) => ({
      ...resolveOptions(source), saveKey: source.saveKey ?? options.saveKey,
    }))];
    for (const source of sources) {
      // Read failures must not be mistaken for an empty save during migration.
      const record = await readLegacyRecord<ValueSaveRecord<T>>(source.dbName, source.storeName, source.saveKey);
      if (record?.value !== undefined) return JSON.stringify(record.value);
    }
    return null;
  });
  return {
    async loadGameSave(): Promise<T | undefined> {
      if (!usesAndroidSQLite()) return browser.loadGameSave();
      const value = await storage.getItem(options.saveKey);
      return value === null ? undefined : JSON.parse(value) as T;
    },
    async saveGameState(value: T) {
      if (!usesAndroidSQLite()) return browser.saveGameState(value);
      await storage.setItem(options.saveKey, JSON.stringify(value));
    },
    async clearGameSave() {
      if (!usesAndroidSQLite()) return browser.clearGameSave();
      await storage.removeItem(options.saveKey);
    },
  };
}

export function createGameSessionStorage<T>(options: Omit<GameSaveStorageOptions, "saveKey"> = {}) {
  return createGameSaveStorage<T>({
    ...options,
    saveKey: DEFAULT_SESSION_SAVE_KEY,
    legacySources: options.legacySources?.map((source) => ({
      ...source,
      saveKey: source.saveKey ?? DEFAULT_SESSION_SAVE_KEY
    }))
  });
}

export function deleteGameDatabase(options: IndexedDbStorageOptions = {}) {
  const resolved = resolveOptions(options);

  if (usesAndroidSQLite()) return clearAndroidDatabase(resolved.dbName);

  if (!canUseIndexedDb()) return;
  window.indexedDB.deleteDatabase(resolved.dbName);
}
